import {
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  Optional,
  Output,
  signal,
} from '@angular/core';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map } from 'rxjs';

import { EntrySelectors } from 'src/app/state/entry.selectors';
import { CommonService } from 'src/app/common.service';
import { GoogleAnalyticsService } from 'src/app/google-analytics.service';
import { ConfigService } from 'src/app/config.service';
import { PlatformService } from 'src/app/platform.service';
import { UtilService } from 'src/app/util.service';
import { Chain, PaeDataService } from 'src/app/pae-view/pae-data.service';

import { EntryService } from '../../entry.service';
import { EntryData } from '../../structure-data.models';
import { ViewState } from '../domains/domains.component';
import { DownloadOption } from '../../entry-dropdown/entry-dropdown.component';

interface MappedMacromolecule {
  tedNumber: number;
  displayTitle: string;
  displayChains: string;
  chainLabels: string[];
  source: EntryData;
  groupedSources: EntryData[];
}

interface BiologicalFunctionInfo {
  text: string;
  source: string;
  source_url: string;
}

const BIOLOGICAL_FUNCTION_WORD_LIMIT = 20;
const SEARCH_UNIPROT_ACCESSION_REGEX =
  /^([OPQ][0-9][A-Z0-9]{3}[0-9]|[A-NR-Z][0-9](?:[A-Z][A-Z0-9]{2}[0-9]){1,2})(?:-\d+)?$/i;

@Component({
  selector: 'app-macromolecules-mobile',
  templateUrl: './macromolecules.component.html',
  styleUrl: './macromolecules.component.css',
  standalone: false,
})
export class MacromoleculesComponent {
  @Output() handleSelectedMacromoleculeParent: EventEmitter<{ chainId: string; otherChainIds: string[] }> = new EventEmitter();

  public expanded = signal<boolean>(false);
  public entryData = signal<EntryData>({} as EntryData);
  public complexData = signal<any>({});
  public macromoleculesData = signal<EntryData[]>([]);
  public chainData = signal<Chain[]>([]);
  public activeMacromolecule = signal<EntryData | null>(null);
  // Fragment of a larger UniProt protein (start !== 1) — surfaced next to the UniProt
  // accession in the template (AFPDBE-1445 follow-up).
  public activeMacromoleculeRange = computed(() => this.utilService.getMacromoleculeResidueRange(this.activeMacromolecule()));
  public mappedMacromolecules = signal<MappedMacromolecule[]>([]);
  public currentViewState = signal<ViewState>(ViewState.List);
  public viewStates = ViewState;
  public hydrationReady = false;
  public title = this.entryService.domainTitle;
  public detailTabIndex = signal<number>(0);
  public kbCount = signal<number | null>(null);
  public alsoComponentInCount = signal<number | null>(null);
  public biologicalFunction = signal<BiologicalFunctionInfo>({
    text: 'Data unavailable (function not known)',
    source: 'tooltip',
    source_url: '',
  });
  public isBiologicalFunctionLoading = signal<boolean>(false);
  public isBiologicalFunctionExpanded = signal<boolean>(false);
  public isAlsoComponentInLoading = signal<boolean>(false);
  public isCopySequence = false;
  public chainDropdownOptions: DownloadOption[] = [];
  public chainDropdownSelected = '';

  private readonly destroyRef = inject(DestroyRef);
  private biologicalFunctionCache = new Map<string, BiologicalFunctionInfo>();
  private alsoComponentInCache = new Map<string, number>();
  private experimentalStructureCountCache = new Map<string, number>();
  private activeBiologicalFunctionAccession = '';
  private activeAlsoComponentInAccession = '';
  private activeExperimentalStructuresAccession = '';

  constructor(
    @Optional() private store: Store,
    private entryService: EntryService,
    private platform: PlatformService,
    private paeDataService: PaeDataService,
    private commonService: CommonService,
    private configService: ConfigService,
    private utilService: UtilService,
    public bottomSheetRef: MatBottomSheetRef<MacromoleculesComponent>,
    public gaService: GoogleAnalyticsService,
  ) {
    effect(() => {
      this.chainData.set(this.paeDataService.chains());
      this.rebuildMacromoleculeCards();
    });

    effect(() => {
      const accession = this.activeMacromolecule()?.uniprotAccession;
      this.loadBiologicalFunction(accession);
      this.loadAlsoComponentInCount(accession);
      this.loadExperimentalStructures(accession);
    });
  }

  ngOnInit(): void {
    this.resetToListState();
    this.entryService.updateSelectedDomainTitle('Macromolecules');

    if (this.platform.isBrowserPlatform()) {
      this.hydrationReady = true;
    }

    combineLatest([
      this.store.select(EntrySelectors.entryData).pipe(filter(Boolean)),
      this.store.select(EntrySelectors.complexData).pipe(),
      this.store.select(EntrySelectors.macromoleculesData).pipe(),
    ])
      .pipe(
        map(([entryData, complexData, macromoleculesData]) => {
          this.entryData.set(entryData);
          this.complexData.set(complexData || {});
          this.macromoleculesData.set(macromoleculesData || []);
          this.rebuildMacromoleculeCards();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public resetToListState(): void {
    this.currentViewState.set(ViewState.List);
    this.detailTabIndex.set(0);
  }

  public navigateToDetail(macromolecule: MappedMacromolecule): void {
    this.currentViewState.set(ViewState.Detail);
    this.detailTabIndex.set(0);
    this.activeMacromolecule.set(macromolecule.source);
    this.syncChainDropdownForActiveMacromolecule();
    this.emitMolstarChainSelection();
    this.entryService.updateSelectedDomainTitle(macromolecule.displayTitle);
    this.gaService.eventEmitter(
      'mobile_macromolecule_select',
      'mobile_macromolecules_interaction',
      'tap',
      'mobile_macromolecule_selected',
      'Select a macromolecule from list of macromolecules'
    );
  }

  public goBackToList(): void {
    this.currentViewState.set(ViewState.List);
    this.detailTabIndex.set(0);
    this.entryService.updateSelectedDomainTitle('Macromolecules');
  }

  public onDetailTabChange(index: number): void {
    this.detailTabIndex.set(index);
  }

  public getAveragePlddtDisplay(macromolecule: EntryData | null): string {
    const value = macromolecule?.globalMetricValue;
    if (value === null || value === undefined) {
      return 'Not available';
    }

    return `${value} (${this.getPlddtLevel(value)})`;
  }

  public getActiveChainHeading(): string {
    const activeCard = this.getActiveMappedMacromolecule();
    if (!activeCard) {
      return 'Chain data unavailable';
    }

    if (this.showChainDropdown()) {
      return this.getActiveChainTitle();
    }

    if (activeCard.chainLabels.length === 1) {
      return `Chain ${activeCard.chainLabels[0]}`;
    }

    if (activeCard.chainLabels.length > 1) {
      return `Chain ${activeCard.chainLabels.join('/')}`;
    }

    return activeCard.displayChains;
  }

  public getActiveSequence(): string {
    return this.activeMacromolecule()?.sequence || this.activeMacromolecule()?.uniprotSequence || '';
  }

  public showChainDropdown(): boolean {
    const activeCard = this.getActiveMappedMacromolecule();
    if (!activeCard || activeCard.chainLabels.length <= 1 || activeCard.groupedSources.length <= 1) {
      return false;
    }

    const accessions = new Set(
      activeCard.groupedSources
        .map((item) => (item?.uniprotAccession || '').trim())
        .filter((value) => !!value)
    );

    return accessions.size === 1;
  }

  public getActiveChainTitle(): string {
    const activeCard = this.getActiveMappedMacromolecule();
    if (!activeCard) {
      return 'Chain';
    }

    if (this.showChainDropdown()) {
      return this.chainDropdownSelected || this.chainDropdownOptions[0]?.name || 'Chain';
    }

    if (activeCard.chainLabels.length > 0) {
      return `Chain ${activeCard.chainLabels[0]}`;
    }

    return activeCard.displayChains || 'Chain data unavailable';
  }

  public onChainDropdownSelect(optionName: string): void {
    this.chainDropdownSelected = optionName;
    this.syncActiveMacromoleculeForSelectedChain(optionName);
    this.emitMolstarChainSelection();
  }

  public getPlddtLevel(plddt: number): string {
    return plddt > 90
      ? 'Very high'
      : plddt > 70
      ? 'High'
      : plddt > 50
      ? 'Low'
      : 'Very low';
  }

  public getIpSaeLevel(ipSAE: number | null | undefined): string {
    if (ipSAE === null || ipSAE === undefined) {
      return 'Not available';
    }

    return ipSAE >= 0.8
      ? 'Very high'
      : ipSAE >= 0.7
      ? 'Confident'
      : ipSAE >= 0.6
      ? 'Low'
      : 'Very low';
  }

  public getShortenedBiologicalFunctionText(): string {
    const text = this.biologicalFunction().text;
    if (!text) {
      return '';
    }

    const words = text.split(' ');
    if (words.length <= BIOLOGICAL_FUNCTION_WORD_LIMIT) {
      return text;
    }

    return `${words.slice(0, BIOLOGICAL_FUNCTION_WORD_LIMIT).join(' ')} ...`;
  }

  public canShowMoreBiologicalFunction(): boolean {
    const text = this.biologicalFunction().text;
    if (!text) {
      return false;
    }

    return text.split(' ').length > BIOLOGICAL_FUNCTION_WORD_LIMIT;
  }

  public toggleBiologicalFunctionText(): void {
    this.isBiologicalFunctionExpanded.update((value) => !value);
  }

  public getExperimentalStructuresText(): string {
    const activeMacromolecule = this.activeMacromolecule();
    const count = this.kbCount();

    if (!activeMacromolecule?.uniprotAccession || count === null || count === undefined) {
      return 'Not available';
    }

    if (count === 0) {
      return 'None available in the PDB';
    }

    return `${count} structures in PDB`;
  }

  public hasInterfaceQuality(): boolean {
    const data = this.complexData();
    return [
      data?.complexPredictionAccuracy_ipSAE,
      data?.complexPredictionAccuracy_ipTM,
      data?.complexPredictionAccuracy_pDockQ2,
      data?.complexPredictionAccuracy_LIS,
    ].some((value) => value !== null && value !== undefined && value !== '');
  }

  public copySequence(sequence: string): void {
    const text = `${sequence || ''}`;
    this.utilService.copy(text);
    this.isCopySequence = true;

    setTimeout(() => {
      this.isCopySequence = false;
    }, 3000);

    this.gaService.eventEmitter('sequence_copy', 'interaction', 'click', 'copy_sequence', 'Click on Copy sequence');
  }

  public closeBottomSheet(): void {
    this.bottomSheetRef.dismiss();
    this.entryService.updateSelectedComponent(null);
    this.entryService.updateSelectedTabName('');
  }

  public toggleBottomsheetHeight(): void {
    this.expanded.update((value) => !value);
    const container = document.querySelector('.custom-bottom-sheet') as HTMLElement;

    if (container) {
      container.style.height = this.expanded() ? '50%' : '10%';
    }

    let tag = 'bottomsheet_toggle_up';
    let gtagmsg = 'Opening bottomsheet up';

    if (!this.expanded()) {
      tag = 'bottomsheet_toggle_down';
      gtagmsg = 'Collapsing bottomsheet down';
    }

    this.gaService.eventEmitter('bottomsheet_toggle', 'mobile_overlay_interaction', 'tap', tag, gtagmsg);
  }

  private rebuildMacromoleculeCards(): void {
    const macromolecules = this.macromoleculesData() || [];
    const chains = this.chainData() || [];
    // uniprotAccession alone isn't a reliable group key: a polyprotein cleaved into separate
    // chains (e.g. NS3/NS5) can report the SAME accession for genuinely different chains with
    // different residue ranges (AFPDBE-1435) — see UtilService.groupMacromoleculeChains.
    const groups = this.utilService.groupMacromoleculeChains(macromolecules);

    const mapped = groups.map((group, index) => {
      // Sort this chain-group's raw rows by chain ID so `representative` (and therefore
      // groupedSources[0], the default activeMacromolecule on first load) is always the
      // alphabetically-first chain — the same ordering chainLabels/the chain dropdown
      // already use below. See the desktop macromolecules-tab fix for the same issue
      // (AFPDBE-1418).
      const sortedGroup = [...group].sort((a, b) =>
        ((a as any)?.chainId || '').localeCompare((b as any)?.chainId || '')
      );
      const representative = sortedGroup[0];
      const chainLabels = this.getChainLabelsForGroup(sortedGroup, chains).sort((left, right) => left.localeCompare(right));

      return {
        tedNumber: index + 1,
        displayTitle:
          representative?.uniprotDescription || representative?.uniprotAccession || `Macromolecule ${index + 1}`,
        displayChains: chainLabels.length > 0 ? `Chain ${chainLabels.join(', ')}` : 'Chain data unavailable',
        chainLabels,
        source: representative,
        groupedSources: sortedGroup,
      };
    });

    mapped.sort((left, right) => {
      const leftChain = left.chainLabels[0] || '';
      const rightChain = right.chainLabels[0] || '';
      return leftChain.localeCompare(rightChain);
    });
    mapped.forEach((item, index) => {
      item.tedNumber = index + 1;
    });

    this.mappedMacromolecules.set(mapped);

    if (mapped.length === 0) {
      this.activeMacromolecule.set(null);
      this.chainDropdownOptions = [];
      this.chainDropdownSelected = '';
      return;
    }

    const active = this.activeMacromolecule();
    const previousChainId = this.getCurrentSelectedChainId();
    // Match by the physically selected chain first — uniprotAccession alone is ambiguous
    // when two cards share an accession (AFPDBE-1435); description is a secondary tiebreaker
    // before giving up and defaulting to the first card.
    const selectedCard =
      mapped.find((item) =>
        item.groupedSources.some((row) => ((row as any)?.chainId || '').trim() === previousChainId)
      ) ||
      mapped.find((item) => item.source?.uniprotAccession === active?.uniprotAccession && item.source?.uniprotDescription === active?.uniprotDescription) ||
      mapped[0];
    const nextActive = this.getSourceForChainInCard(selectedCard, previousChainId) || selectedCard.source;
    this.activeMacromolecule.set(nextActive);
    this.syncChainDropdownForActiveMacromolecule();
  }

  private getChainLabelsForGroup(group: EntryData[], chains: Chain[]): string[] {
    const unique = new Set<string>();
    const normalizedNames = new Set(group.map((item) => this.normalizeText(item?.uniprotDescription)).filter(Boolean));

    for (const item of group) {
      const chainIdFromEntry = (item as any)?.chainId as string | undefined;
      if (chainIdFromEntry) {
        unique.add(chainIdFromEntry);
      }
    }

    for (const chain of chains) {
      if (normalizedNames.has(this.normalizeText(chain?.name)) && chain?.label_asym_id) {
        unique.add(chain.label_asym_id);
      }
    }

    if (unique.size === 0 && group.length === 1 && chains.length === this.macromoleculesData().length) {
      const source = group[0];
      const sourceIndex = this.macromoleculesData().findIndex((item) => item === source);
      const chainFromIndex = chains[sourceIndex]?.label_asym_id;

      if (chainFromIndex) {
        unique.add(chainFromIndex);
      }
    }

    return Array.from(unique);
  }

  public onRelatedEntryClick(linkType: string): void {
    this.gaService.emitRelatedEntryClick(linkType, this.entryData()?.isComplex, this.entryData()?.assemblyType);
  }

  private getActiveMappedMacromolecule(): MappedMacromolecule | undefined {
    const active = this.activeMacromolecule();
    if (!active) {
      return undefined;
    }

    return this.mappedMacromolecules().find((item) =>
      item.source === active ||
      item.groupedSources.includes(active) ||
      // Last-resort fallback: accession alone is ambiguous when two cards share one
      // (AFPDBE-1435), so also require description to match before accepting it.
      (!!active.uniprotAccession &&
        item.source?.uniprotAccession === active.uniprotAccession &&
        item.source?.uniprotDescription === active.uniprotDescription)
    );
  }

  private extractChainIdFromDropdownOption(optionName: string): string {
    return optionName.replace(/^Chain\s+/i, '').trim();
  }

  private getCurrentSelectedChainId(): string | undefined {
    const selected = this.extractChainIdFromDropdownOption(this.chainDropdownSelected || '');
    return selected || ((this.activeMacromolecule() as any)?.chainId as string | undefined);
  }

  private getSourceForChainInCard(card: MappedMacromolecule | undefined, chainId: string | undefined): EntryData | undefined {
    if (!card) {
      return undefined;
    }

    if (!chainId) {
      return card.source;
    }

    const normalized = chainId.trim().toLowerCase();
    return card.groupedSources.find(
      (item) => (((item as any)?.chainId as string | undefined) || '').trim().toLowerCase() === normalized
    ) || card.source;
  }

  private syncActiveMacromoleculeForSelectedChain(optionName: string): void {
    const activeCard = this.getActiveMappedMacromolecule();
    const selectedChainId = this.extractChainIdFromDropdownOption(optionName);

    if (!activeCard || !selectedChainId) {
      return;
    }

    const matchedSource = this.getSourceForChainInCard(activeCard, selectedChainId);
    if (matchedSource) {
      this.activeMacromolecule.set(matchedSource);
    }
  }

  private syncChainDropdownForActiveMacromolecule(): void {
    const activeCard = this.getActiveMappedMacromolecule();
    const chainLabels = activeCard?.chainLabels || [];
    const previousSelection = this.chainDropdownSelected;

    this.chainDropdownOptions = chainLabels.map((label) => {
      const normalizedLabel = (label || '').trim();
      return {
        name: `Chain ${normalizedLabel}`,
        url: `chain-${normalizedLabel}`,
        downloadable: false,
      };
    });

    const hasPreviousSelection = this.chainDropdownOptions.some((option) => option.name === previousSelection);
    this.chainDropdownSelected = hasPreviousSelection ? previousSelection : this.chainDropdownOptions[0]?.name || '';

    if (!this.chainDropdownSelected) {
      return;
    }

    const selectedChainId = this.extractChainIdFromDropdownOption(this.chainDropdownSelected);
    const matchedSource = this.getSourceForChainInCard(activeCard, selectedChainId);
    if (matchedSource) {
      this.activeMacromolecule.set(matchedSource);
    }
  }

  private loadBiologicalFunction(accession: string | undefined): void {
    if (!accession) {
      this.isBiologicalFunctionLoading.set(false);
      this.isBiologicalFunctionExpanded.set(false);
      this.biologicalFunction.set(this.defaultBiologicalFunction());
      return;
    }

    const cached = this.biologicalFunctionCache.get(accession);
    if (cached) {
      this.isBiologicalFunctionLoading.set(false);
      this.isBiologicalFunctionExpanded.set(false);
      this.biologicalFunction.set(cached);
      return;
    }

    this.activeBiologicalFunctionAccession = accession;
    this.isBiologicalFunctionLoading.set(true);

    const uniprotApiUrl = this.configService.getConfig().uniprotApiUrl + accession;
    this.commonService.getApiData(uniprotApiUrl).subscribe({
      next: (uniprotData) => {
        if (this.activeBiologicalFunctionAccession !== accession) {
          return;
        }

        const parsed = this.parseBiologicalFunction(uniprotData);
        this.biologicalFunctionCache.set(accession, parsed);
        this.biologicalFunction.set(parsed);
        this.isBiologicalFunctionLoading.set(false);
        this.isBiologicalFunctionExpanded.set(false);
      },
      error: () => {
        if (this.activeBiologicalFunctionAccession !== accession) {
          return;
        }

        this.biologicalFunction.set(this.defaultBiologicalFunction());
        this.isBiologicalFunctionLoading.set(false);
        this.isBiologicalFunctionExpanded.set(false);
      },
    });
  }

  private loadAlsoComponentInCount(accession: string | undefined): void {
    if (!accession) {
      this.isAlsoComponentInLoading.set(false);
      this.alsoComponentInCount.set(null);
      return;
    }

    const cached = this.alsoComponentInCache.get(accession);
    if (cached !== undefined) {
      this.isAlsoComponentInLoading.set(false);
      this.alsoComponentInCount.set(cached);
      return;
    }

    const query = this.buildSearchQueryForAccession(accession);
    const auth = this.configService.getConfig().apiKey ? `&key=${this.configService.getConfig().apiKey}` : '';
    const searchApiUrl = `${this.configService.getConfig().searchApiUrl}?q=${query}&type=main&start=0&rows=1${auth}`;

    this.activeAlsoComponentInAccession = accession;
    this.isAlsoComponentInLoading.set(true);

    this.commonService.getApiData(searchApiUrl, { isInternal: true }).subscribe({
      next: (apiData) => {
        if (this.activeAlsoComponentInAccession !== accession) {
          return;
        }

        const numFound = typeof apiData?.numFound === 'number' ? apiData.numFound : 0;
        this.alsoComponentInCache.set(accession, numFound);
        this.alsoComponentInCount.set(numFound);
        this.isAlsoComponentInLoading.set(false);
      },
      error: () => {
        if (this.activeAlsoComponentInAccession !== accession) {
          return;
        }

        this.alsoComponentInCount.set(0);
        this.isAlsoComponentInLoading.set(false);
      },
    });
  }

  private loadExperimentalStructures(accession: string | undefined): void {
    if (!accession) {
      this.kbCount.set(null);
      return;
    }

    const cached = this.experimentalStructureCountCache.get(accession);
    if (cached !== undefined) {
      this.kbCount.set(cached);
      return;
    }

    this.activeExperimentalStructuresAccession = accession;
    const apiUrl = `https://www.ebi.ac.uk/pdbe/search/pdb/select?group=true&group.field=pdb_id&group.ngroups=true&rows=0&omitHeader=true&json.nl=map&q=uniprot_accession:${accession}&wt=json`;
    this.commonService.getApiData(apiUrl).subscribe({
      next: (apiData) => {
        if (this.activeExperimentalStructuresAccession !== accession) {
          return;
        }

        const count = apiData?.grouped?.pdb_id?.ngroups ?? 0;
        this.experimentalStructureCountCache.set(accession, count);
        this.kbCount.set(count);
      },
      error: () => {
        if (this.activeExperimentalStructuresAccession !== accession) {
          return;
        }

        this.kbCount.set(0);
      },
    });
  }

  private buildSearchQueryForAccession(accession: string): string {
    const trimmed = accession.trim();
    const query = SEARCH_UNIPROT_ACCESSION_REGEX.test(trimmed)
      ? `(text:${trimmed}*)`
      : `text:${trimmed}`;

    return encodeURIComponent(query);
  }

  private parseBiologicalFunction(uniprotData: any): BiologicalFunctionInfo {
    const bioFunction = this.defaultBiologicalFunction();
    let catalyticActivity: string | undefined;

    if (uniprotData && Array.isArray(uniprotData.comments)) {
      const functionComment = uniprotData.comments.find(
        (comment) => comment.type === 'FUNCTION' && comment.text?.length
      );

      if (functionComment) {
        const functionText = functionComment.text?.[0]?.value;
        if (functionText) {
          bioFunction.text = functionText.endsWith('.') ? functionText : `${functionText}.`;
          bioFunction.source = 'UniProt';
          bioFunction.source_url = `https://www.uniprot.org/uniprot/${uniprotData.accession}`;
        }
      }

      for (const comment of uniprotData.comments) {
        if (comment.type === 'CATALYTIC_ACTIVITY') {
          catalyticActivity = comment?.reaction?.name;
        }
      }
    }

    if (bioFunction.text === 'Data unavailable (function not known)' && catalyticActivity !== undefined) {
      bioFunction.text = `Catalytic activity: ${catalyticActivity}`;
    }

    if (bioFunction.text !== 'Data unavailable') {
      const pubmed = /PubMed:([0-9]+)/g;
      bioFunction.text = bioFunction.text.replace(
        pubmed,
        '<a href="https://www.uniprot.org/citations/$1" target="_blank">PubMed:$1</a>'
      );
    }

    return bioFunction;
  }

  private defaultBiologicalFunction(): BiologicalFunctionInfo {
    return {
      text: 'Data unavailable (function not known)',
      source: 'tooltip',
      source_url: '',
    };
  }

  private normalizeText(text: string | undefined): string {
    return (text || '').trim().toLowerCase();
  }

  private emitMolstarChainSelection(): void {
    const selectedChainId = (((this.activeMacromolecule() as any)?.chainId as string | undefined) || '').trim();
    if (!selectedChainId) {
      return;
    }

    const allChainIds = Array.from(
      new Set(
        (this.macromoleculesData() || [])
          .map((item) => (((item as any)?.chainId as string | undefined) || '').trim())
          .filter((id) => !!id)
      )
    );

    const otherChainIds = allChainIds.filter((id) => id !== selectedChainId);
    this.handleSelectedMacromoleculeParent.emit({ chainId: selectedChainId, otherChainIds });
  }
}